import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Separator, Spinner, Textarea } from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { type InputHTMLAttributes, useEffect, useId, useMemo, useState } from 'react';
import { type UseFormRegisterReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { FieldError } from '@/components/ui/field-error';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { MARKETS_QUERY_KEY, usePerpOrderLimits, usePositionLimitsList } from '@/hooks/queries/markets/use-markets';
import { getErrorMessage } from '@/lib/api/error';
import {
  buildMarketLimitsRequest,
  DEFAULT_PRICE_COLLAR_PERCENTAGE,
  formatLimitAmount,
  type Market,
  type MarketLimitsFormValues,
  type PerpOrderLimit,
  POSITION_LIMIT_SEGMENT_LABELS,
  POSITION_LIMIT_SEGMENTS,
  type PositionLimit,
  toMarketLimitsFormValues,
  updateMarketLimits,
} from '@/services/api/markets';
import { buildMarketLimitsFieldsSchema, COMMENT_SCHEMA, describeAmount } from './market-limits-schema';

const buildMarketLimitsFormSchema = (initial: MarketLimitsFormValues) => buildMarketLimitsFieldsSchema(initial).extend({ comment: COMMENT_SCHEMA });

type MarketLimitsFormSchema = z.infer<ReturnType<typeof buildMarketLimitsFormSchema>>;

const PRICE_COLLAR_HINT = `How far an order price may sit from the mark price before the exchange rejects it. Bids and asks alike. Exchange default: ${DEFAULT_PRICE_COLLAR_PERCENTAGE}%.`;

interface LimitFieldProps {
  id: string;
  label: string;
  /** What the current value means; gives way to the error while the field is invalid */
  hint: string;
  error?: string;
  inputMode: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  placeholder?: string;
  registration: UseFormRegisterReturn;
}

const LimitField = ({ id, label, hint, error, inputMode, placeholder, registration }: LimitFieldProps) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input aria-describedby={`${id}-hint`} aria-invalid={Boolean(error)} id={id} inputMode={inputMode} placeholder={placeholder} type="text" {...registration} />
    <div id={`${id}-hint`}>{error ? <FieldError message={error} /> : <p className="text-xs text-muted-foreground">{hint}</p>}</div>
  </div>
);
LimitField.displayName = 'LimitField';

const ORDER_LIMITS_LOAD_FAILED_NOTE = 'The current order limits could not be loaded. Leave these fields blank to keep what the exchange has.';
const POSITION_LIMITS_LOAD_FAILED_NOTE = 'The position limits could not be loaded, so they cannot be set here.';
const NO_POSITION_LIMITS_NOTE = 'The exchange reports no position limits for this market, so there is nothing to set here.';

interface MarketLimitsFormProps {
  market: Market;
  /** null when the exchange lists no order limits for the market, or they could not be loaded */
  orderLimits: PerpOrderLimit | null;
  hasOrderLimitsLoadFailed: boolean;
  /** undefined when the exchange reports no position ladder for the market, or it could not be loaded */
  positionLimit: PositionLimit | undefined;
  hasPositionLimitsLoadFailed: boolean;
  /** Carried over when the form is rebuilt after a failed save */
  initialComment: string;
  /** Refetches the exchange's limits and rebuilds the form from them, keeping the comment */
  onSaveFailed: (comment: string) => Promise<void>;
  onClose: () => void;
}

/** Mounted once the exchange's limits have loaded, so the values it opens with are the baseline for "edited". */
const MarketLimitsForm = ({
  market,
  orderLimits,
  hasOrderLimitsLoadFailed,
  positionLimit,
  hasPositionLimitsLoadFailed,
  initialComment,
  onSaveFailed,
  onClose,
}: MarketLimitsFormProps) => {
  const fieldId = useId();
  const queryClient = useQueryClient();
  const quoteCode = market.quote.code;

  // Captured once: a background refetch must not move the baseline mid-edit
  const [initialValues] = useState(() => toMarketLimitsFormValues(orderLimits, positionLimit));
  const formSchema = useMemo(() => buildMarketLimitsFormSchema(initialValues), [initialValues]);

  const form = useForm<MarketLimitsFormSchema>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: { ...initialValues, comment: initialComment },
  });
  const { errors, isSubmitting, isValid } = form.formState;
  const values = form.watch();

  const maxOrderValueHint = (): string => {
    const amount = describeAmount(values.maxOrderValue.trim(), quoteCode);
    return amount ? `Orders worth more than ${amount} are rejected. Cannot be removed once set.` : `No cap on a single order's notional (price × quantity, in ${quoteCode}).`;
  };

  const handleSave = form.handleSubmit(async (formValues) => {
    try {
      await updateMarketLimits(buildMarketLimitsRequest(market.id, formValues, initialValues));
    } catch (error) {
      // Failure keeps the dialog open. A later limit can fail after an earlier one was applied,
      // so the form is rebuilt from what the exchange now enforces: an edit made against the
      // old baseline could otherwise be mistaken for "unchanged" and silently not sent.
      toast.error(`Error updating limits for ${market.id}: ${getErrorMessage(error)}`);
      await onSaveFailed(formValues.comment);
      return;
    }
    toast.success(`Successfully updated limits for ${market.id}`);
    // Every markets query: the exchange's limits, and the instruments bop-api mirrors them into — which clears any drift flag
    void queryClient.invalidateQueries({ queryKey: MARKETS_QUERY_KEY });
    onClose();
  });

  return (
    <form className="flex flex-col gap-5" noValidate onSubmit={handleSave}>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-3 text-sm font-medium">Order limits</legend>
        {hasOrderLimitsLoadFailed ? <p className="text-xs text-amber-600 dark:text-amber-500">{ORDER_LIMITS_LOAD_FAILED_NOTE}</p> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <LimitField
            error={errors.priceCollarPercentage?.message}
            hint={PRICE_COLLAR_HINT}
            id={`${fieldId}-collar`}
            inputMode="decimal"
            label="Price Collar %"
            registration={form.register('priceCollarPercentage')}
          />
          <LimitField
            error={errors.maxOrderValue?.message}
            hint={maxOrderValueHint()}
            id={`${fieldId}-value`}
            inputMode="numeric"
            label={`Max Order Value (${quoteCode})`}
            placeholder="No cap"
            registration={form.register('maxOrderValue')}
          />
        </div>
      </fieldset>

      <Separator />

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-3 text-sm font-medium">Max position per segment</legend>
        {positionLimit ? (
          <>
            <p className="text-xs text-muted-foreground">
              The largest position, in {quoteCode} notional, a customer of each segment may hold. A blank segment uses the market default of{' '}
              <span className="font-medium tabular-nums text-foreground">
                {formatLimitAmount(positionLimit.baseMaxPos)} {quoteCode}
              </span>
              , which is fixed when the market is listed. A per-user override still takes precedence.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              {POSITION_LIMIT_SEGMENTS.map((segment) => (
                <LimitField
                  error={errors[segment]?.message}
                  hint={describeAmount(values[segment].trim(), quoteCode) ?? 'Uses the market default'}
                  id={`${fieldId}-${segment}`}
                  inputMode="numeric"
                  key={segment}
                  label={POSITION_LIMIT_SEGMENT_LABELS[segment]}
                  placeholder="Market default"
                  registration={form.register(segment)}
                />
              ))}
            </div>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">{hasPositionLimitsLoadFailed ? POSITION_LIMITS_LOAD_FAILED_NOTE : NO_POSITION_LIMITS_NOTE}</p>
        )}
      </fieldset>

      <Separator />

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-comment`}>Comment (required for audit trail)</Label>
        <Textarea id={`${fieldId}-comment`} {...form.register('comment')} />
        <p className="text-xs text-muted-foreground">{values.comment.length} characters</p>
        <FieldError message={errors.comment?.message} />
      </div>

      <DialogFooter>
        <Button onClick={onClose} type="button" variant="outline">
          Cancel
        </Button>
        <Button className="gap-2" disabled={!isValid || isSubmitting} type="submit" variant="primary">
          {isSubmitting ? <Spinner className="size-4" /> : null}
          Save
        </Button>
      </DialogFooter>
    </form>
  );
};
MarketLimitsForm.displayName = 'MarketLimitsForm';

interface MarketLimitsDialogProps {
  market: Market;
  onClose: () => void;
}

/**
 * Every limit of a futures market in one editor: the price collar, the max order value and the
 * max position per customer segment. Only the limits the operator edits are sent, under one
 * comment and one audit log. Mounted only while open, so every open starts from the exchange's
 * current values.
 */
export const MarketLimitsDialog = ({ market, onClose }: MarketLimitsDialogProps) => {
  const queryClient = useQueryClient();
  // Bumped after a failed save, which rebuilds the form from the refetched limits
  const [formAttempt, setFormAttempt] = useState({ key: 0, comment: '' });
  const orderLimitsQuery = usePerpOrderLimits(market.id);
  // The table's query, so normally already cached
  const positionLimitsQuery = usePositionLimitsList();
  const positionLimit = positionLimitsQuery.data?.find((limit) => limit.symbol === market.id);

  // Non-404 load failure: say so and open with the order limits blank, which sends nothing
  // unless the operator types a value (404 never reaches here — the service maps it to null)
  useEffect(() => {
    if (orderLimitsQuery.isError) toast.error('Failed to load existing order limits');
  }, [orderLimitsQuery.isError]);

  const handleSaveFailed = async (comment: string) => {
    // Resolves once the limits queries have refetched, so the rebuilt form opens on fresh values
    await queryClient.invalidateQueries({ queryKey: MARKETS_QUERY_KEY });
    setFormAttempt((previous) => ({ key: previous.key + 1, comment }));
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="size-5" />
            Edit Limits - {market.id}
          </DialogTitle>
        </DialogHeader>

        {orderLimitsQuery.isLoading || positionLimitsQuery.isLoading ? (
          <LoadingPanel className="py-10" label="Loading limits" />
        ) : (
          <MarketLimitsForm
            hasOrderLimitsLoadFailed={orderLimitsQuery.isError && orderLimitsQuery.data === undefined}
            hasPositionLimitsLoadFailed={positionLimitsQuery.isError && positionLimitsQuery.data === undefined}
            initialComment={formAttempt.comment}
            key={formAttempt.key}
            market={market}
            onClose={onClose}
            onSaveFailed={handleSaveFailed}
            orderLimits={orderLimitsQuery.data ?? null}
            positionLimit={positionLimit}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
MarketLimitsDialog.displayName = 'MarketLimitsDialog';
